// Etiket zaman serisi grafiği — ApexCharts.
//
// Kontrol panelindeki UretimGrafigi ile aynı kütüphane ve aynı renk disiplini:
// ApexCharts değerleri doğrudan SVG özniteliğine bastığı için `var(--navy-600)`
// orada çözülmez, renkler LİTERAL yazılır ve karşılığı yorumda durur.
//
// Çizilen şey bir "ham seri" DEĞİL, sunucunun kovalanmış özeti (§ EtiketAnalizi):
//   · kalın çizgi = kovanın ortalaması
//   · ince çizgiler = kovanın en az / en çok değeri
// Zarf şart: 7 günlük aralıkta bir kova 42 dakika tutuyor ve yalnız ortalama
// çizilirse o kovadaki ani sıçrama (alarm anı) grafikte hiç görünmezdi.
//
// Zarf dolu bant (`rangeArea`) yerine üç çizgi: panelde (UretimGrafigi) zaten
// çalıştığı doğrulanmış desen bu ve taşınan bilgi aynı — ortalama, tavan, taban.

import { lazy, Suspense, useMemo } from 'react'
import type { ApexOptions } from 'apexcharts'
import { tr } from '../../lib/trFormat'
import { kovaEtiketi } from './zamanAraligi'
import type { EtiketAnalizi } from './api'
import { YEREL } from './grafikYerel'

const Chart = lazy(() => import('react-apexcharts'))

const RENK = {
  cizgi: '#17427E', // --navy-600
  zarf: '#9DB6D8', // --navy-600'ün açığı; en az/en çok çizgileri
  izgara: '#ECF0F6', // --line-soft
  govde: '#5A6B80', // --muted
} as const

const YAZI = 'Montserrat, system-ui, sans-serif'
const MONO = '"IBM Plex Mono", monospace'

/** Ondalık basamak: birim küçük değerliyse daha çok basamak gerekir. */
function basamakSec(min: number | null, maks: number | null): number {
  if (min === null || maks === null) return 2
  const buyuk = Math.max(Math.abs(min), Math.abs(maks))
  if (buyuk >= 1000) return 0
  if (buyuk >= 10) return 1
  if (buyuk >= 1) return 2
  return 3
}

export default function EtiketGrafigi({
  analiz,
  zoomVar,
  zoomIste,
  zoomSifirla,
}: {
  analiz: EtiketAnalizi
  zoomVar: boolean
  zoomIste: (bas: number, son: number) => void
  zoomSifirla: () => void
}) {
  const { kovalar, istatistik, etiket } = analiz
  const basamak = basamakSec(istatistik.min, istatistik.maks)
  const birim = etiket.birim ? ` ${etiket.birim}` : ''

  // Zaman ekseni gerçek zaman: kovalar eşit aralıklı olsa da veri akmayan
  // aralıkta kova HİÇ üretilmiyor (GROUP BY boş kovayı doğurmaz). Kategori
  // ekseni kullanılsaydı kesinti sıkışır, grafik kesintisiz akmış gibi görünürdü.
  const nokta = (v: number | null | undefined) => (v === null || v === undefined ? null : v)

  const seriler = useMemo(() => [
    {
      name: 'Ortalama',
      data: kovalar.map((k) => ({ x: new Date(k.zaman).getTime(), y: nokta(k.ortalama) })),
    },
    {
      name: 'En çok',
      data: kovalar.map((k) => ({ x: new Date(k.zaman).getTime(), y: nokta(k.maks) })),
    },
    {
      name: 'En az',
      data: kovalar.map((k) => ({ x: new Date(k.zaman).getTime(), y: nokta(k.min) })),
    },
  ], [kovalar])

  const secenekler: ApexOptions = useMemo(() => ({
    chart: {
      type: 'line', height: 300, fontFamily: YAZI,
      toolbar: { show: false },
      animations: { enabled: false }, background: 'transparent',
      ...YEREL,
      // Durum çizelgesiyle AYNI zoom kalıbı — iki grafik aynı yerde nöbetleşe
      // çiziliyor, birinde olup diğerinde olmayan bir jest özelliği "bazen
      // kaybolan" yapardı. Gerekçeler DurumCizelgesi.tsx'te.
      zoom: { enabled: true, type: 'x', allowMouseWheelZoom: true },
      events: {
        zoomed: (_c, o) => {
          const x = o?.xaxis
          if (x && typeof x.min === 'number' && typeof x.max === 'number') {
            zoomIste(x.min, x.max)
          }
        },
      },
    },
    colors: [RENK.cizgi, RENK.zarf, RENK.zarf],
    stroke: { curve: 'straight', width: [1.9, 1, 1], dashArray: [0, 3, 3] },
    markers: { size: 0 },
    dataLabels: { enabled: false },
    legend: {
      show: true, position: 'top', horizontalAlign: 'right',
      fontSize: '11px', itemMargin: { horizontal: 8 },
    },
    grid: { borderColor: RENK.izgara, strokeDashArray: 3, padding: { left: 4, right: 8 } },
    xaxis: {
      type: 'datetime',
      labels: {
        style: { colors: RENK.govde, fontSize: '10px', fontFamily: MONO },
        datetimeUTC: false,
      },
      axisBorder: { color: RENK.izgara },
      axisTicks: { color: RENK.izgara },
    },
    yaxis: {
      labels: {
        style: { colors: RENK.govde, fontSize: '10px', fontFamily: MONO },
        formatter: (v: number) => tr(v, basamak),
      },
      // Birim y ekseni BAŞLIĞI olarak denendi: ApexCharts 6.10'da `yaxis.title`
      // nesnesi verildiğinde grafik sessizce boş çerçeveye düşüyor (seri, eksen,
      // metin hiç üretilmiyor; konsola da bir şey düşmüyor). İkiye bölerek
      // bulundu. Birim zaten altbilgide, ipucunda ve tabloda yazıyor.
    },
    tooltip: {
      shared: true,
      x: { format: 'dd.MM.yyyy HH:mm' },
      y: { formatter: (v: number) => (v === null ? '—' : `${tr(v, basamak)}${birim}`) },
    },
    noData: {
      text: 'Bu aralıkta ölçüm yok',
      style: { color: RENK.govde, fontSize: '12px', fontFamily: YAZI },
    },
  }), [basamak, birim, zoomIste])

  return (
    <div style={{ padding: '4px 4px 0' }}>
      {zoomVar && (
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '0 8px 6px', fontSize: 11,
          }}
        >
          <span className="cell-sub">
            Yakınlaştırılmış görünüm — veriler bu aralık için yeniden hesaplandı.
          </span>
          <button
            type="button"
            className="btn btn-ghost"
            style={{ padding: '2px 10px', fontSize: 11, minHeight: 0 }}
            onClick={zoomSifirla}
          >
            Yakınlaştırmayı sıfırla
          </button>
        </div>
      )}
      <Suspense
        fallback={
          <div className="cell-sub" style={{ padding: 24, textAlign: 'center' }}>
            Grafik yükleniyor…
          </div>
        }
      >
        <Chart options={secenekler} series={seriler} type="line" height={300} />
      </Suspense>
      <div className="cell-sub" style={{ fontSize: 10.5, padding: '0 8px 8px' }}>
        {tr(kovalar.length, 0)} nokta · her nokta {kovaEtiketi(analiz.kovaSn)}'lık kova ·
        kalın çizgi ortalama, kesikli çizgiler o kovadaki en az ve en çok
        {etiket.birim ? ` · birim ${etiket.birim}` : ''}
      </div>
    </div>
  )
}
